import { useTranslation } from 'react-i18next'
import { CardFooter } from '@/components/ui/card'
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination'

interface ListPaginationProps {
  page: number
  pageSize: number
  totalCount: number
  onPageChange: (page: number) => void
  /** Shown instead of "page x of y" when everything fits on one page. */
  summary?: React.ReactNode
}

/** Page numbers around the current page, with ellipses standing in for the gaps. */
function pageItems(page: number, totalPages: number): Array<number | 'ellipsis'> {
  return Array.from({ length: totalPages }, (_, i) => i + 1)
    .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
    .reduce<Array<number | 'ellipsis'>>((acc, p, idx, arr) => {
      if (idx > 0 && p - (arr[idx - 1] as number) > 1) acc.push('ellipsis')
      acc.push(p)
      return acc
    }, [])
}

/**
 * Card footer for a paged list: the page summary on the left, page links on
 * the right. Collapses to just the summary while there is only one page, and
 * renders nothing for an empty list.
 */
export function ListPagination({
  page,
  pageSize,
  totalCount,
  onPageChange,
  summary,
}: ListPaginationProps) {
  const { t } = useTranslation()
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize))

  if (totalCount === 0) return null

  if (totalPages <= 1) {
    return (
      <CardFooter>
        <span className="text-xs text-(--ad-text-muted)">
          {summary ?? t('common.pageOf', { page: 1, total: 1, count: totalCount })}
        </span>
      </CardFooter>
    )
  }

  return (
    <CardFooter className="flex items-center justify-between gap-4">
      <span className="text-xs text-(--ad-text-muted)">
        {t('common.pageOf', { page, total: totalPages, count: totalCount })}
      </span>
      <Pagination>
        <PaginationContent>
          <PaginationItem>
            <PaginationPrevious
              onClick={() => onPageChange(Math.max(1, page - 1))}
              aria-disabled={page === 1}
              className={page === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
            />
          </PaginationItem>
          {pageItems(page, totalPages).map((item, idx) =>
            item === 'ellipsis' ? (
              <PaginationItem key={`e-${idx}`}>
                <PaginationEllipsis />
              </PaginationItem>
            ) : (
              <PaginationItem key={item}>
                <PaginationLink
                  isActive={item === page}
                  onClick={() => onPageChange(item)}
                  className="cursor-pointer"
                >
                  {item}
                </PaginationLink>
              </PaginationItem>
            ),
          )}
          <PaginationItem>
            <PaginationNext
              onClick={() => onPageChange(Math.min(totalPages, page + 1))}
              aria-disabled={page === totalPages}
              className={page === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
            />
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    </CardFooter>
  )
}
